import { QueryClient } from "@tanstack/react-query";
import { createRouter } from "@tanstack/react-router";
import { routeTree } from "./routeTree.gen";
import { setupQueryPersistence, shouldPreloadAggressively } from "./lib/perf";

export const getRouter = () => {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        // Treat data as fresh for 5 minutes — returning to a screen
        // shows the previous data instantly (no skeleton flash).
        staleTime: 5 * 60_000,
        gcTime: 30 * 60_000,
        // Don't re-fetch when the tab regains focus or the network
        // blips — these are the main causes of "the page keeps refreshing".
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        retry: 1,
        // Resolve from cache immediately even when offline — instant restore.
        networkMode: "offlineFirst",
      },
    },
  });

  // Restore last session's data from localStorage on client boot — turns
  // a cold open into an instant load (no skeletons on returning users).
  setupQueryPersistence(queryClient);

  const aggressive = shouldPreloadAggressively();

  const router = createRouter({
    routeTree,
    context: { queryClient },
    scrollRestoration: true,
    // Preload routes on hover/focus — but only when the network can afford it.
    // Save-Data / 2G users skip preloading to protect their data plan.
    defaultPreload: aggressive ? "intent" : false,
    defaultPreloadStaleTime: 60_000,
    defaultStaleTime: 30_000,
    defaultPendingMs: 150,
    defaultPendingMinMs: 0,
    // Smooth cross-fade between pages in browsers that support it
    // (no-op fallback elsewhere). Removes the harsh "snap" feel.
    defaultViewTransition: true,
  });

  return router;
};
